
  import { defineComponent, ref, onMounted, computed, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import LookupNameIds from '@/store/enums/settingsEnums';

  export default defineComponent({
    name: 'package-validation-rule-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedRuleType = ref('');

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('createNewValidationRule'), [
          translate('settings'),
        ]);

        // Load dropdown data
        await store.dispatch(Actions.GET_ALL_SETTINGS_CLASS_CATEGORY);
        await store.dispatch(Actions.GET_ALL_SETTINGS_LOOKUPS, [
          LookupNameIds.HORSE_TYPE,
          LookupNameIds.CLASS_TYPE,
        ]);
        const ruleTypesResult = await store.dispatch(
          Actions.GET_PACKAGE_VALIDATION_RULE_TYPES
        );

        // Debug: Check if rule types are loaded
        console.log('Rule types result from dispatch:', ruleTypesResult);
        console.log(
          'Rule types in store getter:',
          store.getters.packageValidationRuleTypesList
        );
        console.log('Store state:', store.state.PackageValidationRuleModule);
      });

      const validationSchema = computed(() => {
        const baseSchema: any = {
          name: Yup.string().required(() => translate('nameIsRequired')),
          ruleType: Yup.string().required(() =>
            translate('ruleTypeIsRequired')
          ),
          numberOfCancellation: Yup.number().required(() =>
            translate('numberOfCancellationIsRequired')
          ),
          numberOfDays: Yup.number().required(() =>
            translate('numberOfDaysIsRequired')
          ),
          priority: Yup.number().required(() =>
            translate('priorityIsRequired')
          ),
        };

        // Add conditional validation based on rule type
        if (selectedRuleType.value === 'CLASS_CATEGORY') {
          baseSchema.classCategoryId = Yup.string()
            .nullable()
            .required(() => translate('Class Category is required'));
        } else if (
          selectedRuleType.value === 'PACKAGE_COUNT' ||
          selectedRuleType.value === 'INVALID_PACKAGE'
        ) {
          baseSchema.packageCountMin = Yup.number()
            .nullable()
            .required(() => translate('Package Count Min is required'));
          baseSchema.packageCountMax = Yup.number()
            .nullable()
            .required(() => translate('Package Count Max is required'));
        } else if (selectedRuleType.value === 'HORSE_LESSON_COMBO') {
          baseSchema.horseTypeId = Yup.string()
            .nullable()
            .required(() => translate('Horse Type is required'));
          baseSchema.packageCountMin = Yup.number()
            .nullable()
            .required(() => translate('Package Count Min is required'));
          baseSchema.packageCountMax = Yup.number()
            .nullable()
            .required(() => translate('Package Count Max is required'));
        }

        return Yup.object().shape(baseSchema);
      });

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        // Clean up values based on rule type
        const payload: any = {
          name: values.name,
          ruleType: values.ruleType,
          numberOfCancellation: Number(values.numberOfCancellation),
          numberOfDays: Number(values.numberOfDays),
          numberOfDaysError: values.numberOfDaysError || null,
          priority: Number(values.priority),
          isActive: values.isActive === 'true' || values.isActive === true,
        };

        if (values.ruleType === 'CLASS_CATEGORY') {
          payload.classCategoryId = values.classCategoryId;
        } else if (
          values.ruleType === 'PACKAGE_COUNT' ||
          values.ruleType === 'INVALID_PACKAGE'
        ) {
          payload.packageCountMin = Number(values.packageCountMin);
          payload.packageCountMax = Number(values.packageCountMax);
        } else if (values.ruleType === 'HORSE_LESSON_COMBO') {
          payload.horseTypeId = values.horseTypeId;
          if (values.lessonTypeId) payload.lessonTypeId = values.lessonTypeId;
          if (values.packageCountMin)
            payload.packageCountMin = Number(values.packageCountMin);
          if (values.packageCountMax)
            payload.packageCountMax = Number(values.packageCountMax);
        }

        const result = await store.dispatch(
          Actions.CREATE_PACKAGE_VALIDATION_RULE,
          payload
        );
        const errors = store.getters.getErrors;
        const hasErrors = errors && Object.keys(errors).length > 0;

        if (hasErrors || !result) {
          const [errorName] = Object.keys(errors || {});
          const error = errors[errorName];
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('Successfully Created Validation Rule'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'package-validation-rule-listing' });
          });
        }
      };

      const lookupsList = computed(() => store.getters.allLookupsList);

      const horseTypes = computed(() =>
        lookupsList.value.filter((lookup) => {
          return lookup.lookupNameId === 16; // HORSE_TYPE
        })
      );

      const lessonTypes = computed(() =>
        lookupsList.value.filter((lookup) => {
          return lookup.lookupNameId === 18; // CLASS_TYPE
        })
      );

      const ruleTypesComputed = computed(
        () => store.getters.packageValidationRuleTypesList
      );

      // Debug watcher
      watch(
        ruleTypesComputed,
        (newVal) => {
          console.log('Rule types changed:', newVal);
        },
        { immediate: true }
      );

      return {
        onSubmitCreate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        can,
        selectedRuleType,
        allClassCategories: computed(() => store.getters.allClassCategoryList),
        ruleTypes: computed(() => store.getters.packageValidationRuleTypesList),
        horseTypes,
        lessonTypes,
      };
    },
  });
